import {
    Colors,
    decimalToLocalizedString,
    isMonetaryValueDataObject,
    monetaryToLocalizedString,
    MonetaryValue,
    Typography,
    TypographyWithDecorator,
} from 'ComponentLibrary'
import { HeadlineVariants } from 'ComponentLibrary/src/styles/moduleSpacings'
import { styled, useTheme } from '@mui/material'

export type NumericalInputType = 'float' | 'monetary' | 'integer' | 'plain' | 'percentage'

// This piece of beauty comes from https://www.typescriptlang.org/docs/handbook/2/conditional-types.html
type ValueType<T> = T extends 'monetary'
    ? MonetaryValue | null
    : T extends 'float' | 'integer' | 'plain' | 'percentage'
      ? number | null
      : never

export type BasedLabeledValueProps = {
    value?: string
    label: string
    variant?: HeadlineVariants | 'bodyTextMedium'
    valueProps?: ValueProps
}

export type NumericLabeledValueProps<T extends NumericalInputType> = Omit<BasedLabeledValueProps, 'value'> & {
    value?: ValueType<T>
    type: T
    unit?: string
    locale?: string
}

type ValueProps = {
    color?: string
    decorator?: string
}

export function BaseLabeledValue({ value, label, valueProps, variant }: BasedLabeledValueProps) {
    return (
        <LabeledValueWrapper>
            {valueProps?.decorator ? (
                <TypographyWithDecorator variant={variant} data-testid="decorated-value" {...valueProps}>
                    {value}
                </TypographyWithDecorator>
            ) : (
                <Typography variant={variant} color={valueProps?.color}>
                    {value}
                </Typography>
            )}
            <Typography variant="bodyTextXSmall" style={{ wordBreak: 'normal' }} color={Colors.customtext.black54}>
                {label}
            </Typography>
        </LabeledValueWrapper>
    )
}

const LabeledValueWrapper = styled('div')`
    width: 100%;
    white-space: nowrap;
`

export function NumericLabeledValue<T extends NumericalInputType>({
    value,
    type,
    unit,
    label,
    variant = 'headline1',
    valueProps,
    locale,
}: NumericLabeledValueProps<T>) {
    const formattedValue = toFormattedValue({ value, type, unit, locale })
    return <BaseLabeledValue value={formattedValue} label={label} variant={variant} valueProps={valueProps} />
}

export type HighlightedLabeledValueProps = Omit<BasedLabeledValueProps, 'valueProps'> & {
    decorator?: ValueProps['decorator']
    color?: string
}

export function HighlightedLabeledValue({ decorator, color, ...props }: HighlightedLabeledValueProps) {
    const theme = useTheme()
    return (
        <BaseLabeledValue
            {...props}
            valueProps={{
                color: color ?? theme.palette.secondary.main,
                decorator,
            }}
        />
    )
}

type ToFormattedValueProps<T extends NumericalInputType> = {
    value?: ValueType<T>
    type: T
    unit?: string
    locale?: string
}
export function toFormattedValue<T extends NumericalInputType>({ value, type, unit, locale = 'de-DE' }: ToFormattedValueProps<T>): string {
    let formattedValue
    if (isMonetaryValueDataObject(value)) {
        formattedValue =
            monetaryToLocalizedString(value, false, false, locale) !== null ? monetaryToLocalizedString(value, false, false, locale) : '-'
    } else if (value === null || value === undefined) {
        formattedValue = '-'
    } else {
        // eslint-disable-next-line  @typescript-eslint/switch-exhaustiveness-check
        switch (type) {
            case 'percentage' as const:
                formattedValue = decimalToLocalizedString(Number(value) * 100, false, 2, undefined, locale)
                if (!unit) {
                    unit = '%'
                }
                break
            case 'float' as const:
                formattedValue = decimalToLocalizedString(value, false, 2, undefined, locale)
                break
            case 'integer' as const:
                formattedValue = decimalToLocalizedString(value, true, 0, undefined, locale)
                break
            case 'plain' as const:
                formattedValue = `${value}`
                break
            default:
                formattedValue = '-'
        }
    }
    return `${formattedValue}${unit && formattedValue !== '-' ? ` ${unit}` : ''}`
}
